import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@/components/DatePicker';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Helper from '@/lib/helper';
import { AlertCircleIcon, Asterisk, Download, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar"
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { AgentClaims } from '@/types/AgentClaims';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from "@/components/ui/alert"
import { Skeleton } from '@/components/ui/skeleton';

interface ClaimForm {
    date: Date;
    renName: string;
    lofNo: string;
    leaderName: string;
    type: number;
    commAmount: string;
    sstPaidBy: number | null;
    sstAmount: string;
    amount: string;
    propertyAddress: string;
    balanceReturnTo: string;
    fullName: string;
    coAgencyName: string;
    tenancyPreparedBy: string;
    referral: string;
    submittedBy: string;
    acknowledgedBy: string;
    rentMonths?: number | null;
    commissionPercentage?: number | null;
}

interface SplitAgent {
    agentId: string;
    agentName: string;
    avatar: string;
    percentage: number;
    isNew?: boolean;
    renNo: string;
    editable: boolean;
}

interface UplineSplit {
    level: number;
    rank?: number;
    agents: SplitAgent[];
}

const toNumericId = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const fileUploadFields = [
    { key: 'tenancyAgreement', label: 'Tenancy Agreement / Sales & Purchase Agreement', required: true  },
    { key: 'agreementToRent', label: 'Agreement To Rent / To Purchase', required: true  },
    { key: 'authorizationToLet', label: 'Authorization To Let / To Sell', required: false },
    { key: 'ssm', label: 'SSM / IC Copy', required: false },
    { key: 'form49', label: 'Form 49 / IC Copy', required: false },
    { key: 'invoiceFromAgent', label: 'Invoice From Agent',  required: true  },
    { key: 'invoiceFromAgency', label: 'Invoice From Agency', required: true  },
    { key: 'paymentSlip', label: 'Payment Slip', required: true  },
];

const ClaimsEdit = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const { claimId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState<ClaimForm>({
        date: new Date(),
        renName: agent ? (agent.firstName + ' ' + agent.lastName) : '',
        lofNo: '',
        leaderName: agent?.leader ? `${agent.leader.firstName} ${agent.leader.lastName}` : '',
        type: 1,
        sstPaidBy: null,
        amount: '0.00',
        commAmount: '0.00',
        sstAmount: '0.00',
        propertyAddress: '',
        balanceReturnTo: '',
        fullName: '',
        coAgencyName: '',
        tenancyPreparedBy: '',
        referral: '',
        submittedBy: agent ? `${agent.renNo}` : '',
        acknowledgedBy: '',
    });
    const [claim, setClaim] = useState<AgentClaims | null>(null);
    const [uplineSplits, setUplineSplits] = useState<UplineSplit[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const handleGoBack = () => navigate(-1);

    const getClaim = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/me/claims/${claimId}`);
            if (!response.data.status) throw new Error(response.data.error);
            setClaim(response.data.claim);
        } catch (err: any) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setLoading(false);
        }
    };

    const getResourceUrl = (path: string) => `${import.meta.env.VITE_AWS_S3}/${path}`;
    const getResourceFileName = (path: string, fallback: string) => decodeURIComponent(path.split('?')[0].split('/').pop() || fallback);

    const openDocument = (path: string) => {
        window.open(getResourceUrl(path), '_blank');
    };

    const downloadDocument = async (path?: string | null, fallback = 'claim-document') => {
        if (!path) return;
        const url = getResourceUrl(path);
        const fileName = getResourceFileName(path, fallback);

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Unable to fetch file');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            toast.success('Document downloaded successfully');
        } catch {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    useEffect(() => {
        getClaim();
    }, []);

    useEffect(() => {
        if (claim) {
            setForm({
                date: new Date(claim.date),
                renName: claim.renName,
                lofNo: claim.lofNo,
                leaderName: claim.leaderName,
                type: claim.type,
                sstPaidBy: claim.sstPaidBy ?? null,
                amount: String(claim.amount),
                commAmount: String(claim.commAmount),
                sstAmount: String(claim.sstAmount),
                rentMonths: claim.rentMonths,
                commissionPercentage: claim.commissionPercentage,
                propertyAddress: claim.propertyAddress,
                balanceReturnTo: String(claim.balanceReturnTo ?? ''),
                fullName: String(claim.fullName ?? ''),
                coAgencyName: String(claim.coAgencyName ?? ''),
                tenancyPreparedBy: String(claim.tenancyPreparedBy),
                referral: claim.referral ?? '',
                submittedBy: String(claim.submittedBy ?? ''),
                acknowledgedBy: claim.acknowledgedBy || '',
            });

            if (claim.uplineSplits) {
                setUplineSplits(claim.uplineSplits);
            }
        }
    }, [claim]);

    const claimedStatus = Number(claim?.claimed);
    const claimAgentId = toNumericId(claim?.agentId);
    const filteredSplits = (uplineSplits || [])
        .map(split => ({
            ...split,
            agents: split.agents.filter(item => toNumericId(item.agentId) === claimAgentId),
        }))
        .filter(split => split.agents.length > 0);
    const levelOneAgent = (uplineSplits || [])
        .find(split => Number(split.rank ?? split.level) === 1)
        ?.agents[0];
    const displaySplits = claimedStatus === 0
        ? (filteredSplits.length > 0
            ? filteredSplits
            : (levelOneAgent ? [{
                level: 1,
                rank: 1,
                agents: [levelOneAgent],
            }] : []))
        : (uplineSplits || []);

    if (loading) {
        return (
            <div className="">
                <Skeleton className="h-8 w-64 mb-4" />
                <Skeleton className="h-6 w-full max-w-2xl mx-auto mb-8" />
                <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <div className="flex gap-2">
                        <Skeleton className="h-9 w-24" />
                        <Skeleton className="h-9 w-32" />
                    </div>
                    <Skeleton className="h-10 w-40" />
                    <Skeleton className="h-10 w-32" />
                    <div className="grid md:grid-cols-2 gap-4">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-20 w-full" />
                    </div>
                    <Skeleton className="h-20 w-full" />
                    <div className="grid md:grid-cols-2 gap-4">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-20 w-full" />
                    </div>
                    <Skeleton className="h-32 w-full" />
                    <div className="space-y-3">
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-40 w-full" />
                        <Skeleton className="h-40 w-full" />
                    </div>
                    <Skeleton className="h-10 w-full" />
                </div>
            </div>
        );
    }

    if (!claim) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <AlertCircleIcon className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Claim not found</p>
                <Button variant="outline" onClick={handleGoBack} className="mt-4">Go Back</Button>
            </div>
        );
    }

    return (
        <div className="">
            <h5 className='h5 uppercase'>CLAIM YOUR MONEY</h5>
            <h6 className='h6 text-center font-bold mt-5'>SALT & LIGHT REALTY SDN BHD CLAIM FORM</h6>
            {claim.claimed === 0 && (
                <Alert className="mt-5 bg-blue-50 border-blue-200 text-blue-800">
                    <AlertCircleIcon className="text-blue-600" />
                    <AlertTitle>Info</AlertTitle>
                    <AlertDescription>
                        Submitted form is not allowed to be edited. If you wish to edit the details,
                        you will need to delete this and create a new one.
                    </AlertDescription>
                </Alert>
            )}

            <div className="mt-5">
                <div className='mb-5'>
                    <Label>Date:</Label>
                    <DatePicker className='mt-2' value={form.date} disabled />
                </div>
                <div>
                    <div className='mb-5 w-full'>
                        <Label>
                            Type
                            <Asterisk size={12} className="text-red-500" />
                        </Label>
                        <div className='flex items-center gap-2 mt-2'>
                            <Button className='cursor-not-allowed' size={'sm'} variant={form.type === 1 ? 'default' : 'outline'} disabled>Sale</Button>
                            <Button className='cursor-not-allowed' size={'sm'} variant={form.type === 2 ? 'default' : 'outline'} disabled>Rent</Button>
                        </div>
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>REN Name (as per I/C)<Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.renName} disabled />
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Leader Name<Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.leaderName} disabled />
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Letter of Offer ({form.type === 1 ? 'Sale' : 'Rent'}) Number<Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.lofNo} disabled />
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>{form.type === 1 ? 'Sale' : 'Rent'} Amount<Asterisk size={12} className="text-red-500" /></Label>
                        <div className="relative mt-2 flex items-center gap-0">
                            <div className='flex items-center absolute left-2 shadow-none'>
                                <Badge variant={'secondary'}>RM</Badge>
                            </div>
                            <Input type="text" className='text-sm pl-12 w-full' value={Helper.formatWithDecimal(Number(form.amount))} disabled />
                        </div>
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>{form.type === 1 ? 'Commission %' : 'Rent Months'}<Asterisk size={12} className="text-red-500" /></Label>
                        <Input
                            type="text"
                            inputMode="numeric"
                            className='mt-2 text-sm w-full'
                            value={Helper.formatWithDecimal(Number(form.type === 1 ? (form.commissionPercentage || '') : (form.rentMonths || '')))}
                            disabled
                        />
                    </div>
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>8% SST Paid By<Asterisk size={12} className="text-red-500" /></Label>
                        <div className='flex gap-2 mt-2'>
                            <Button className='cursor-not-allowed' size={'sm'} variant={form.sstPaidBy === 1 ? 'default' : 'outline'} disabled>Owner</Button>
                            <Button className='cursor-not-allowed' size={'sm'} variant={form.sstPaidBy === 2 ? 'default' : 'outline'} disabled>Agent</Button>
                            <Button className='cursor-not-allowed' size={'sm'} variant={form.sstPaidBy === 3 ? 'default' : 'outline'} disabled>Forfeited</Button>
                        </div>
                        <div className="relative mt-2 flex items-center gap-0">
                            <div className='flex items-center absolute left-2 shadow-none'>
                                <Badge variant={'secondary'}>RM</Badge>
                            </div>
                            <Input type="text" className='text-sm pl-12 w-full' value={Helper.formatWithDecimal(Number(form.sstAmount))} disabled />
                        </div>
                    </div>
                </div>
                <div className='mb-5'>
                    <Label>Total Commission Amount</Label>
                    <div className="relative mt-2 flex items-center gap-0">
                        <div className='flex items-center absolute left-2 shadow-none'>
                            <Badge variant={'secondary'}>RM</Badge>
                        </div>
                        <Input type="text" className="text-sm pl-12 w-full font-semibold" value={Helper.formatWithDecimal(Number(form.commAmount))} disabled />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Final commission amount after all calculations</p>
                </div>
                <div className="md:col-span-2 mb-5">
                    <Label>Property Address<Asterisk size={12} className="text-red-500" /></Label>
                    <Textarea className='mt-2' value={form.propertyAddress} disabled />
                </div>
                <div className='md:flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Balance of E.D / Amount Return to<Asterisk size={12} className="text-red-500" /></Label>
                        <Select value={form.balanceReturnTo} disabled>
                            <SelectTrigger className='w-full mt-2'><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Owner</SelectItem>
                                <SelectItem value="2">Purchaser</SelectItem>
                                <SelectItem value="3">Tenant</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Full Name<Asterisk size={12} className="text-red-500" /></Label>
                        <Input className='mt-2 text-sm w-full' type="text" value={form.fullName} disabled />
                    </div>
                </div>
                <div className='mb-5'>
                    <Label>Co-Agency Name (As per Invoice)<Asterisk size={12} className="text-red-500" /></Label>
                    <Input className='mt-2 text-sm w-full' type="text" value={form.coAgencyName} disabled />
                </div>
                <div className='flex gap-2'>
                    <div className='mb-5 w-full'>
                        <Label>Tenancy Prepared By<Asterisk size={12} className="text-red-500" /></Label>
                        <Select value={form.tenancyPreparedBy} disabled>
                            <SelectTrigger className='w-full mt-2'><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Negotiator</SelectItem>
                                <SelectItem value="2">Admin</SelectItem>
                                <SelectItem value="3">Owner</SelectItem>
                                <SelectItem value="4">Tenant</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='mb-5 w-full'>
                        <Label>Referral (as per IC)<Asterisk size={12} className="text-red-500" /></Label>
                        <Input type="text" className='mt-2 text-sm w-full' value={form.referral} disabled />
                    </div>
                </div>

                <hr className='my-5' />
                <div className='mb-5'>
                    <Label className='text-lg font-semibold'>Commission Distribution</Label>
                    {displaySplits.length > 0 ? (
                        <div className='mt-3 space-y-4'>
                            {displaySplits.map((split, levelIndex) => (
                                <div key={levelIndex} className='border rounded-lg p-4'>
                                    <div className='flex items-center justify-between mb-3'>
                                        <div className='flex items-center gap-2'>
                                            <Badge variant='outline' className='font-mono'>L{split.rank || split.level}</Badge>
                                        </div>
                                    </div>
                                    <div className='space-y-2'>
                                        {split.agents.map((agent, agentIndex) => (
                                            <div key={agentIndex} className='md:flex items-start gap-3 p-3 bg-gray-50 rounded-lg'>
                                                <div className='flex gap-3 mb-2 md:mb-0 md:flex-1'>
                                                    <Avatar className='h-10 w-10'>
                                                        <AvatarImage src={agent.avatar ? `${import.meta.env.VITE_AWS_S3}/${agent.avatar}` : ''} />
                                                        <AvatarFallback>
                                                            {agent.agentName ? agent.agentName.split(' ').map(n => n[0]).join('') : '?'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className='flex-1'>
                                                        <p className='text-sm font-medium'>{agent.agentName || 'New Agent'}</p>
                                                        <p className='text-xs'>{agent.renNo || ''}</p>
                                                        <p className='text-xs text-gray-500'>My share</p>
                                                    </div>
                                                </div>
                                                <div className='flex flex-col gap-2 md:min-w-[200px]'>
                                                    <div className='flex items-center gap-2'>
                                                        <div className='flex items-center gap-1'>
                                                            <Input
                                                                type="text"
                                                                inputMode="numeric"
                                                                value={Number(agent.percentage)?.toFixed(2) || '0.00'}
                                                                className="text-sm text-center w-20"
                                                                maxLength={6}
                                                                disabled
                                                            />
                                                            <span className='text-sm font-medium'>%</span>
                                                        </div>
                                                        <div className='flex-1 text-right'>
                                                            <div className='text-sm font-semibold text-primary'>
                                                                RM {Helper.formatWithDecimal(Number(((parseFloat(form.commAmount.replace(/,/g, '')) * Number(agent.percentage)) / 100).toFixed(2)))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded'>
                                                        RM {Helper.formatWithDecimal(Number(form.commAmount.replace(/,/g, '')))} × {Number(agent.percentage).toFixed(2)}% = RM {Helper.formatWithDecimal(Number(((parseFloat(form.commAmount.replace(/,/g, '')) * Number(agent.percentage)) / 100).toFixed(2)))}
                                                    </div>
                                                    <div className='flex flex-wrap justify-end gap-2 mt-1'>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openDocument(agent.receiptPath)}
                                                                className="h-7 px-2 text-xs border-muted-foreground/30 text-muted-foreground hover:bg-muted"
                                                                disabled={!agent.receiptPath}
                                                            >
                                                                <FileText className="w-3 h-3" />
                                                                Receipt
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => downloadDocument(agent.receiptPath, 'receipt')}
                                                                className="h-7 w-7 p-0 border-muted-foreground/30 text-muted-foreground hover:bg-muted"
                                                                disabled={!agent.receiptPath}
                                                            >
                                                                <Download className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openDocument(agent.paymentVoucherPath)}
                                                                className="h-7 px-2 text-xs border-muted-foreground/30 text-muted-foreground hover:bg-muted"
                                                                disabled={!agent.paymentVoucherPath}
                                                            >
                                                                <FileText className="w-3 h-3" />
                                                                Voucher
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => downloadDocument(agent.paymentVoucherPath, 'payment-voucher')}
                                                                className="h-7 w-7 p-0 border-muted-foreground/30 text-muted-foreground hover:bg-muted"
                                                                disabled={!agent.paymentVoucherPath}
                                                            >
                                                                <Download className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-gray-500 mt-2'>No commission distribution found</p>
                    )}
                </div>

                <hr className='my-5' />
                <div className='mb-5'>
                    <Label>Submitted By (REN)</Label>
                    <Input type="text" className="mt-2 text-sm md:w-auto w-full" value={agent.renNo} disabled />
                </div>

                <hr className='my-5' />
                <div className="space-y-6">
                    <h3 className="text-lg font-semibold">Uploaded Documents</h3>
                    {fileUploadFields.map((field, index) => {
                        // attachments[key] is now an array of { id, path }
                        const files: { id: number; path: string }[] = claim.attachments?.[field.key] ?? [];
                        return (
                            <div key={field.key} className="flex items-start gap-4">
                                <div className="flex-shrink-0 text-sm">{index + 1}.</div>
                                <div className="flex-1">
                                    <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                                        {field.label}
                                        {!field.required && (
                                            <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                                        )}
                                    </Label>
                                    {files.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {files.map((f, fileIndex) => (
                                                <div key={f.id} className="flex items-center gap-1">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => openDocument(f.path)}
                                                        className="flex gap-2 hover:bg-accent"
                                                    >
                                                        <FileText className="w-4 h-4" />
                                                        {files.length > 1 ? `Document ${fileIndex + 1}` : 'View Document'}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => downloadDocument(f.path, files.length > 1 ? `${field.label}-${fileIndex + 1}` : field.label)}
                                                        className="h-9 w-9 p-0 hover:bg-accent"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">No document uploaded</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-4">
                <Button variant="outline" className='uppercase' onClick={handleGoBack}>Back</Button>
            </div>
        </div>
    );
};

export default ClaimsEdit;
